import { Fragment, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Divider,
  Group,
  Loader,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconBrain, IconChevronDown, IconChevronRight, IconPlayerPlay, IconRefresh, IconTrash, IconX } from "@tabler/icons-react";

import { api, ApiClientError } from "../api/client";
import type { KronosMarketStatus, KronosSignatureInfo } from "../api/kronosSchemas";
import { ErrorState, LoadingState } from "../components/ui";
import { KronosHorizonCard } from "../components/KronosHorizonCard";
import { KRONOS_FIELD_DESCRIPTIONS, KRONOS_MODEL_OPTIONS, KRONOS_SIGNATURE_HINT } from "../lib/kronosFields";
import { monoStyle } from "../theme";

function relTime(iso: string | null): string {
  if (!iso) return "never";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "never";
  const secs = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

function jobColor(status: string): string {
  switch (status) {
    case "running": return "blue";
    case "succeeded": return "green";
    case "failed": return "red";
    case "cancelled": return "gray";
    default: return "gray";
  }
}

// Editable Kronos config fields: ONLY the six inference params that form the
// cache signature (precompute key). The gate post-filters — kronos_check_enabled,
// kronos_require_direction, kronos_min_confidence — are NOT edited here: they
// don't affect the signature/precompute (they're tuned in the simulators and
// set as live defaults on the Config page). This editor preserves them verbatim
// via the ...base spread. Kept as strings/numbers in form state.
type KronosForm = {
  kronos_model: string;
  kronos_lookback: number | string;
  kronos_candle_timeframe_seconds: number | string;
  kronos_sample_count: number | string;
  kronos_temperature: number | string;
  kronos_top_p: number | string;
};

const numOr = (v: number | string, d: number): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

function formFromConfig(cfg: Record<string, unknown>): KronosForm {
  const b = (k: string, d: boolean) => (typeof cfg[k] === "boolean" ? (cfg[k] as boolean) : d);
  const n = (k: string, d: number) => numOr(cfg[k] as number | string, d);
  const s = (k: string, d: string) => (typeof cfg[k] === "string" && cfg[k] ? (cfg[k] as string) : d);
  return {
    kronos_model: s("kronos_model", "small"),
    kronos_lookback: n("kronos_lookback", 400),
    kronos_candle_timeframe_seconds: n("kronos_candle_timeframe_seconds", 60),
    kronos_sample_count: n("kronos_sample_count", 5),
    kronos_temperature: n("kronos_temperature", 1.0),
    kronos_top_p: n("kronos_top_p", 0.9),
  };
}

// Mimic Python `%g` so the preview signature matches the cached rows exactly.
function formatG(x: number): string {
  if (!Number.isFinite(x)) return String(x);
  return parseFloat(x.toPrecision(6)).toString();
}

// Expand a [min, max] decision-time range (seconds) at `step` into the explicit
// list of decision_time_seconds to precompute (inclusive). Mirrors the Auto
// Simulator's numeric range sweep. Returns [] when the bounds are invalid so
// the caller can fall back to the active config's single decision time.
function buildDecisionTimes(
  min: number | string,
  max: number | string,
  step: number | string,
): number[] {
  const lo = Math.trunc(numOr(min, 0));
  const hi = Math.trunc(numOr(max, 0));
  const st = Math.max(1, Math.trunc(numOr(step, 1)));
  if (lo <= 0 || hi <= 0) return [];
  const a = Math.min(lo, hi);
  const b = Math.max(lo, hi);
  const out: number[] = [];
  for (let t = a; t <= b; t += st) out.push(t);
  return out;
}

// Collapse a list of ints into compact contiguous ranges, e.g.
// [240,241,242,250] -> "240–242, 250". Used to summarise the decision-time
// seconds a cache covers without listing all 40+ values.
function compressInts(nums: number[]): string {
  const s = [...new Set(nums)].sort((a, b) => a - b);
  if (s.length === 0) return "—";
  const parts: string[] = [];
  let start = s[0]!;
  let prev = s[0]!;
  for (let i = 1; i <= s.length; i++) {
    const cur = s[i];
    if (cur !== undefined && cur === prev + 1) {
      prev = cur;
      continue;
    }
    parts.push(start === prev ? `${start}` : `${start}–${prev}`);
    if (cur !== undefined) {
      start = cur;
      prev = cur;
    }
  }
  return parts.join(", ");
}

// ISO -> "MM-DD" for compact coverage display.
function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "—";
  const d = new Date(t);
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${mm}-${dd}`;
}

// One row in the cache table = a full signature with its aggregated decision
// times + window coverage (the `signatures` endpoint returns one flat row per
// signature × decision_time, so we fold those back together here).
type CacheTableRow = {
  signature: string;
  model: string;
  lookback: number;
  timeframe: number;
  sample_count: number;
  temperature: number;
  top_p: number;
  /** Settled-windows-only cache (|md=settled) vs decide-late. */
  settled: boolean;
  decisionTimes: number[];
  /** Per-decision-time window counts, sorted by decision time. */
  perSecond: Array<{ dt: number; count: number }>;
  totalRows: number;
  windowMin: string | null;
  windowMax: string | null;
  lastAt: string | null;
};

function groupSignatures(items: KronosSignatureInfo[]): CacheTableRow[] {
  const map = new Map<string, CacheTableRow>();
  for (const it of items) {
    let row = map.get(it.signature);
    if (!row) {
      row = {
        signature: it.signature,
        model: it.model,
        lookback: it.lookback,
        timeframe: it.timeframe,
        sample_count: it.sample_count,
        temperature: it.temperature,
        top_p: it.top_p,
        settled: it.settled,
        decisionTimes: [],
        perSecond: [],
        totalRows: 0,
        windowMin: it.window_start_min,
        windowMax: it.window_start_max,
        lastAt: it.last_precomputed_at,
      };
      map.set(it.signature, row);
    }
    row.decisionTimes.push(it.decision_time_seconds);
    row.perSecond.push({ dt: it.decision_time_seconds, count: it.count });
    row.totalRows += it.count;
    if (it.window_start_min && (!row.windowMin || it.window_start_min < row.windowMin)) {
      row.windowMin = it.window_start_min;
    }
    if (it.window_start_max && (!row.windowMax || it.window_start_max > row.windowMax)) {
      row.windowMax = it.window_start_max;
    }
    if (it.last_precomputed_at && (!row.lastAt || it.last_precomputed_at > row.lastAt)) {
      row.lastAt = it.last_precomputed_at;
    }
  }
  const rows = [...map.values()];
  for (const r of rows) r.perSecond.sort((a, b) => a.dt - b.dt);
  return rows.sort((a, b) => b.totalRows - a.totalRows);
}

// Classify each decision second of a cache as complete / partial / pending.
// `fullRef` is the TRUE full window count for the market (the busiest cached
// second across ALL signatures — i.e. the most-covered cache is a proxy for
// "every window in the market's history"). A second is COMPLETE only when its
// count reaches that reference, so a still-running job whose seconds have only
// reached e.g. 830/18703 windows correctly shows as partial, not green.
// `targetDts` (the running job's decision-time range) forces not-yet-started
// seconds to appear as pending.
function secondsProgress(
  perSecond: Array<{ dt: number; count: number }>,
  targetDts: number[],
  fullRef: number,
): {
  full: number;
  rows: Array<{ dt: number; count: number; state: "complete" | "partial" | "pending" }>;
  completeCount: number;
  totalCount: number;
} {
  const byDt = new Map(perSecond.map((p) => [p.dt, p.count]));
  // Fall back to the signature's own max only when no market-wide reference is
  // available (e.g. a single lone cache), so we never divide by zero.
  const ownMax = perSecond.reduce((m, p) => Math.max(m, p.count), 0);
  const full = fullRef > 0 ? fullRef : ownMax;
  const allDts = [...new Set([...targetDts, ...perSecond.map((p) => p.dt)])].sort((a, b) => a - b);
  const rows = allDts.map((dt) => {
    const count = byDt.get(dt) ?? 0;
    const state: "complete" | "partial" | "pending" =
      count <= 0 ? "pending" : full > 0 && count >= full ? "complete" : "partial";
    return { dt, count, state };
  });
  return {
    full,
    rows,
    completeCount: rows.filter((r) => r.state === "complete").length,
    totalCount: rows.length,
  };
}

// Build the cache signature from the form — mirrors kronosSignature() in the
// data-api precompute manager and ConfigSnapshot.kronos_signature in Python.
function signatureOf(f: KronosForm): string {
  return (
    `${f.kronos_model}` +
    `|lb=${Math.trunc(numOr(f.kronos_lookback, 400))}` +
    `|tf=${Math.trunc(numOr(f.kronos_candle_timeframe_seconds, 60))}` +
    `|sc=${Math.trunc(numOr(f.kronos_sample_count, 5))}` +
    `|T=${formatG(numOr(f.kronos_temperature, 1.0))}` +
    `|tp=${formatG(numOr(f.kronos_top_p, 0.9))}`
  );
}

/**
 * Per-market panel: choose the Kronos cache-signature params and precompute
 * that exact signature. The params are sent straight to the precompute job —
 * this does NOT change the market's live/active config (that stays on the
 * Config page). The form is seeded from the active config as a convenience.
 */
function MarketKronosPanel({ status }: { status: KronosMarketStatus }) {
  const qc = useQueryClient();
  const marketId = status.market_id;
  const [form, setForm] = useState<KronosForm | null>(null);
  const [seededVersion, setSeededVersion] = useState<string | null>(null);
  const [overwrite, setOverwrite] = useState(false);
  // Settled-windows-only mode: forecast each window from its OPEN using only
  // completed prior windows. Produces a SEPARATE cache (|md=settled) so it can
  // be A/B'd against the decide-late cache with identical params in the sim.
  const [settled, setSettled] = useState(false);
  // datetime-local string ("YYYY-MM-DDTHH:mm", local tz). Empty = all history;
  // otherwise precompute every window from this point on.
  const [startFrom, setStartFrom] = useState<string>("");
  // Decision-time range (seconds) to precompute, expanded [min..max] step. Both
  // default to the active config's decision_time_seconds (single value) so the
  // default behaviour is unchanged; widen the range to warm multiple entry
  // seconds in one run.
  const [decisionMin, setDecisionMin] = useState<number | string>("");
  const [decisionMax, setDecisionMax] = useState<number | string>("");
  const [decisionStep, setDecisionStep] = useState<number | string>(1);
  // Signatures whose per-second completion breakdown is expanded in the table.
  const [expandedSigs, setExpandedSigs] = useState<Set<string>>(new Set());
  const toggleExpanded = (sig: string) =>
    setExpandedSigs((prev) => {
      const next = new Set(prev);
      if (next.has(sig)) next.delete(sig);
      else next.add(sig);
      return next;
    });

  const cfgQ = useQuery({
    queryKey: ["markets", marketId, "config-active", "kronos"],
    queryFn: () => api.markets.activeConfig(marketId),
    staleTime: 0,
  });

  // Flat list of every cached signature × decision_time for this market —
  // folded into a per-signature table below so all params + covered decision
  // times are visible at a glance (no more hover-only summaries).
  const sigsQ = useQuery({
    queryKey: ["kronos", "signatures", marketId],
    queryFn: () => api.kronos.signatures(marketId),
    refetchInterval: 8000,
  });

  // Seed the form from the active config (convenience) or defaults. Re-seeds
  // when the active config version changes. The form is NOT saved back to the
  // config — it only drives the precompute run.
  useEffect(() => {
    if (cfgQ.isLoading) return;
    const active = cfgQ.data?.active;
    const key = active?.config_version_id ?? "__defaults__";
    if (key !== seededVersion) {
      setForm(formFromConfig(active?.config ?? {}));
      // Seed the decision-time range from the active config's single decision
      // time so min == max by default (behaviour unchanged unless widened).
      const dt = Math.trunc(numOr((active?.config as Record<string, unknown>)?.decision_time_seconds as number | string, 0));
      setDecisionMin(dt > 0 ? dt : "");
      setDecisionMax(dt > 0 ? dt : "");
      setDecisionStep(1);
      setSeededVersion(key);
    }
  }, [cfgQ.data, cfgQ.isLoading, seededVersion]);

  const runMut = useMutation({
    mutationFn: () => {
      if (!form) throw new Error("form not ready");
      // Send the six signature params straight to the precompute job. This
      // precomputes THIS exact signature without touching the live config.
      return api.kronos.precompute({
        market_id: marketId,
        model: form.kronos_model as "mini" | "small" | "base",
        lookback: Math.trunc(numOr(form.kronos_lookback, 400)),
        timeframe: Math.trunc(numOr(form.kronos_candle_timeframe_seconds, 60)),
        sample_count: Math.trunc(numOr(form.kronos_sample_count, 5)),
        temperature: numOr(form.kronos_temperature, 1.0),
        top_p: numOr(form.kronos_top_p, 0.9),
        // Expand the [min..max] step range into explicit decision times. Empty
        // (no config) falls back to the active config decision time server-side.
        decision_times: buildDecisionTimes(decisionMin, decisionMax, decisionStep),
        overwrite,
        settled,
        // datetime-local has no tz; interpret as local and send UTC ISO.
        start: startFrom ? new Date(startFrom).toISOString() : undefined,
      });
    },
    onSuccess: (job) => {
      notifications.show({
        color: "blue",
        title: "Precompute started",
        message: `${job.market_id} · ${job.signature}`,
      });
      qc.invalidateQueries({ queryKey: ["kronos", "status"] });
      qc.invalidateQueries({ queryKey: ["kronos", "signatures", marketId] });
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        title: "Could not start precompute",
        message: err instanceof ApiClientError ? err.message : String(err),
      });
    },
  });

  const cancelMut = useMutation({
    mutationFn: () => api.kronos.cancel(marketId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["kronos", "status"] }),
  });

  const deleteMut = useMutation({
    mutationFn: (signature: string) =>
      api.kronos.deleteCache({ market_id: marketId, signature }),
    onSuccess: (res) => {
      notifications.show({
        color: "green",
        title: "Cache deleted",
        message: `${res.deleted.toLocaleString()} rows removed`,
      });
      qc.invalidateQueries({ queryKey: ["kronos", "signatures", marketId] });
      qc.invalidateQueries({ queryKey: ["kronos", "status"] });
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        title: "Could not delete cache",
        message: err instanceof ApiClientError ? err.message : String(err),
      });
    },
  });

  if (cfgQ.isLoading) return <LoadingState label={`Loading ${marketId} config...`} />;
  if (cfgQ.isError) return <ErrorState error={cfgQ.error as Error} onRetry={() => cfgQ.refetch()} />;
  if (!form) return <LoadingState label="Preparing form..." />;

  const hasConfig = Boolean(cfgQ.data?.active);
  const running = status.job?.status === "running";
  const set = <K extends keyof KronosForm>(key: K, value: KronosForm[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  // Preview signature reflects the settled toggle so THIS RUN'S SIGNATURE, the
  // CACHED badge, and the table row highlight all track the mode being run.
  const previewSig = signatureOf(form) + (settled ? "|md=settled" : "");
  const previewCached = status.cached.find((g) => g.signature === previewSig) ?? null;
  const decisionTimes = buildDecisionTimes(decisionMin, decisionMax, decisionStep);
  const decisionCount = decisionTimes.length;
  const cacheRows = groupSignatures(sigsQ.data?.items ?? []);
  const activeSig = status.active?.signature ?? null;
  // The busiest single (signature × decision-time) cache in the market is the
  // best proxy for "a fully-covered second" (all windows in history). Used as
  // the completion reference so partially-filled seconds don't show as green.
  const marketFull = (sigsQ.data?.items ?? []).reduce((m, it) => Math.max(m, it.count), 0);

  return (
    <Stack gap="md" mt="md">
      <Card withBorder padding="md">
        <Title order={5} mb="sm">Cache signature params</Title>

        <Select
          label="Model"
          description={KRONOS_FIELD_DESCRIPTIONS.kronos_model}
          data={KRONOS_MODEL_OPTIONS}
          value={form.kronos_model}
          onChange={(v) => set("kronos_model", v ?? "small")}
          allowDeselect={false}
          mb="sm"
        />

        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="sm">
          <NumberInput
            label="Lookback (context candles)"
            description={KRONOS_FIELD_DESCRIPTIONS.kronos_lookback}
            min={1}
            max={512}
            step={10}
            value={form.kronos_lookback}
            onChange={(v) => set("kronos_lookback", v)}
          />
          <NumberInput
            label="Candle timeframe (seconds)"
            description={KRONOS_FIELD_DESCRIPTIONS.kronos_candle_timeframe_seconds}
            min={1}
            step={10}
            value={form.kronos_candle_timeframe_seconds}
            onChange={(v) => set("kronos_candle_timeframe_seconds", v)}
          />
          <NumberInput
            label="Sample count"
            description={KRONOS_FIELD_DESCRIPTIONS.kronos_sample_count}
            min={1}
            max={256}
            step={1}
            value={form.kronos_sample_count}
            onChange={(v) => set("kronos_sample_count", v)}
          />
          <NumberInput
            label="Temperature"
            description={KRONOS_FIELD_DESCRIPTIONS.kronos_temperature}
            min={0.1}
            step={0.1}
            decimalScale={2}
            value={form.kronos_temperature}
            onChange={(v) => set("kronos_temperature", v)}
          />
          <NumberInput
            label="Top-p"
            description={KRONOS_FIELD_DESCRIPTIONS.kronos_top_p}
            min={0.01}
            max={1}
            step={0.05}
            decimalScale={3}
            value={form.kronos_top_p}
            onChange={(v) => set("kronos_top_p", v)}
          />
        </SimpleGrid>

        <Alert color="blue" variant="light" title="These params define the cache signature" mt="md">
          {KRONOS_SIGNATURE_HINT} Adjust them, then click Run precompute below to warm the cache for
          THIS signature. This does NOT change the market's live config — set that on the Config page.
        </Alert>
      </Card>

      <Card withBorder padding="md">
        <Title order={5} mb="sm">Precompute</Title>

        <Group gap="xl" align="flex-start" mb="sm">
          <Stack gap={2}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Active signature</Text>
            {status.active?.has_config ? (
              <>
                <Group gap={6}>
                  <Badge
                    size="sm"
                    color={status.active.kronos_check_enabled ? "green" : "gray"}
                    variant={status.active.kronos_check_enabled ? "filled" : "light"}
                  >
                    gate {status.active.kronos_check_enabled ? "ON" : "off"}
                  </Badge>
                  <Badge size="sm" color="cyan" variant="light">{status.active.model}</Badge>
                  <Text size="xs" c="dimmed">decision @ {status.active.decision_time_seconds}s</Text>
                </Group>
                <Text size="xs" c="dimmed" style={monoStyle}>{status.active.signature}</Text>
              </>
            ) : (
              <Text size="xs" c="dimmed">no active config</Text>
            )}
          </Stack>

          <Stack gap={2}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Cached (active signature)</Text>
            {status.active_cached ? (
              <>
                <Text size="sm" fw={600}>{relTime(status.active_cached.last_precomputed_at)}</Text>
                <Text size="xs" c="dimmed">{status.active_cached.count} windows cached</Text>
              </>
            ) : (
              <Badge color="yellow" variant="light">never</Badge>
            )}
          </Stack>
        </Group>

        <Group justify="space-between" align="center" mb={4}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            All cached signatures ({cacheRows.length})
          </Text>
          {sigsQ.isFetching ? <Loader size={12} /> : null}
        </Group>
        {cacheRows.length > 0 ? (
          <Table.ScrollContainer minWidth={720} mb="sm">
            <Table striped highlightOnHover withTableBorder verticalSpacing={4} fz="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={28} />
                  <Table.Th>Model</Table.Th>
                  <Table.Th ta="right">LB</Table.Th>
                  <Table.Th ta="right">TF</Table.Th>
                  <Table.Th ta="right">SC</Table.Th>
                  <Table.Th ta="right">T</Table.Th>
                  <Table.Th ta="right">Top-p</Table.Th>
                  <Table.Th>Decision times (s)</Table.Th>
                  <Table.Th ta="right">Windows</Table.Th>
                  <Table.Th>Coverage</Table.Th>
                  <Table.Th>Last run</Table.Th>
                  <Table.Th w={44} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {cacheRows.map((r) => {
                  const isActive = activeSig != null && r.signature === activeSig;
                  const isPreview = r.signature === previewSig;
                  const isRunning = running && status.job?.signature === r.signature;
                  const expanded = expandedSigs.has(r.signature);
                  // For the running signature, measure progress against the
                  // job's full target range so not-yet-started seconds show as
                  // pending; otherwise just the seconds already present.
                  const targetDts = isRunning && status.job ? status.job.decision_times : [];
                  const prog = secondsProgress(r.perSecond, targetDts, marketFull);
                  const allDone = prog.completeCount === prog.totalCount;
                  return (
                    <Fragment key={r.signature}>
                      <Table.Tr bg={isPreview ? "var(--mantine-color-teal-light)" : undefined}>
                        <Table.Td>
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            color="gray"
                            aria-label={expanded ? "Collapse seconds" : "Expand seconds"}
                            onClick={() => toggleExpanded(r.signature)}
                          >
                            {expanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                          </ActionIcon>
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4} wrap="nowrap">
                            <Badge size="xs" variant="light" color="cyan">{r.model}</Badge>
                            <Badge
                              size="xs"
                              variant="light"
                              color={r.settled ? "violet" : "gray"}
                            >
                              {r.settled ? "settled" : "decide-late"}
                            </Badge>
                            {isActive ? (
                              <Badge size="xs" variant="filled" color="green">active</Badge>
                            ) : null}
                          </Group>
                        </Table.Td>
                        <Table.Td ta="right">{r.lookback}</Table.Td>
                        <Table.Td ta="right">{r.timeframe}</Table.Td>
                        <Table.Td ta="right">{r.sample_count}</Table.Td>
                        <Table.Td ta="right">{formatG(r.temperature)}</Table.Td>
                        <Table.Td ta="right">{formatG(r.top_p)}</Table.Td>
                        <Table.Td>
                          <Text size="xs" style={monoStyle}>{compressInts(r.decisionTimes)}</Text>
                          <Text
                            size="xs"
                            c={allDone ? "teal" : isRunning ? "blue" : "dimmed"}
                            fw={isRunning ? 600 : 400}
                          >
                            {prog.completeCount}/{prog.totalCount} second{prog.totalCount === 1 ? "" : "s"} complete
                          </Text>
                        </Table.Td>
                        <Table.Td ta="right">{r.totalRows.toLocaleString()}</Table.Td>
                        <Table.Td>
                          <Text size="xs" c="dimmed">{fmtDate(r.windowMin)} → {fmtDate(r.windowMax)}</Text>
                        </Table.Td>
                        <Table.Td>
                          {isRunning ? (
                            <Group gap={4} wrap="nowrap">
                              <Loader size={12} />
                              <Badge size="xs" variant="light" color="blue">running</Badge>
                            </Group>
                          ) : (
                            <Text size="xs" c="dimmed">{relTime(r.lastAt)}</Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Tooltip label={isRunning ? "Can't delete while running" : "Delete this cache"}>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              size="sm"
                              disabled={isRunning}
                              loading={deleteMut.isPending && deleteMut.variables === r.signature}
                              aria-label="Delete cache"
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `Delete cache "${r.signature}"?\n\nThis removes ${r.totalRows.toLocaleString()} cached rows across ${r.decisionTimes.length} decision time(s) and cannot be undone.`,
                                  )
                                ) {
                                  deleteMut.mutate(r.signature);
                                }
                              }}
                            >
                              <IconTrash size={14} />
                            </ActionIcon>
                          </Tooltip>
                        </Table.Td>
                      </Table.Tr>
                      {expanded ? (
                        <Table.Tr bg="var(--mantine-color-dark-8)">
                          <Table.Td />
                          <Table.Td colSpan={11}>
                            <Text size="xs" c="dimmed" mb={4}>
                              Per-second windows (complete = reaches full history coverage of{" "}
                              {prog.full.toLocaleString()} windows)
                            </Text>
                            <Group gap={4}>
                              {prog.rows.map((s) => (
                                <Tooltip
                                  key={s.dt}
                                  label={`${s.dt}s · ${s.count.toLocaleString()} windows · ${s.state}`}
                                >
                                  <Badge
                                    size="xs"
                                    variant={s.state === "pending" ? "outline" : "light"}
                                    color={
                                      s.state === "complete"
                                        ? "teal"
                                        : s.state === "partial"
                                        ? "yellow"
                                        : "gray"
                                    }
                                    style={monoStyle}
                                  >
                                    {s.dt}
                                  </Badge>
                                </Tooltip>
                              ))}
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        ) : (
          <Text size="xs" c="dimmed" mb="sm">No caches precomputed yet for this market.</Text>
        )}

        <Divider my="sm" />

        <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>Decision time range (seconds)</Text>
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm" mb={6}>
          <NumberInput
            label="Min"
            description="First decision time (s) to precompute"
            min={1}
            step={1}
            value={decisionMin}
            onChange={setDecisionMin}
          />
          <NumberInput
            label="Max"
            description="Last decision time (s), inclusive"
            min={1}
            step={1}
            value={decisionMax}
            onChange={setDecisionMax}
          />
          <NumberInput
            label="Step"
            description="Seconds between decision times"
            min={1}
            step={1}
            value={decisionStep}
            onChange={setDecisionStep}
          />
        </SimpleGrid>
        <Text size="xs" c={decisionCount > 0 ? "dimmed" : "yellow"} mb="sm">
          {decisionCount > 0
            ? `${decisionCount} decision time${decisionCount === 1 ? "" : "s"} → ${decisionCount}× forecasts per window (cost scales linearly).`
            : "Set min/max to precompute a range; empty falls back to the active config's decision time."}
        </Text>

        <Stack gap={8} mb="sm">
          <Checkbox
            size="sm"
            label="Overwrite existing cached rows"
            checked={overwrite}
            onChange={(e) => setOverwrite(e.currentTarget.checked)}
          />
          <Checkbox
            size="sm"
            label="Settled windows only (forecast at window open)"
            description="Forecast each window from its OPEN using only completed prior windows. Writes a separate cache tagged |md=settled so you can A/B it against the decide-late cache with identical params. Set the candle timeframe to the full market window (e.g. 300 for 5m) when using this."
            checked={settled}
            onChange={(e) => setSettled(e.currentTarget.checked)}
          />
        </Stack>

        <Divider my="sm" />

        <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>This run's signature</Text>
        <Group gap={8} mb="sm">
          <Text size="sm" style={monoStyle}>{previewSig}</Text>
          {previewCached ? (
            <Badge size="sm" color="teal" variant="light">
              cached · {previewCached.count} · {relTime(previewCached.last_precomputed_at)}
            </Badge>
          ) : (
            <Badge size="sm" color="yellow" variant="light">not cached</Badge>
          )}
        </Group>

        {!hasConfig ? (
          <Alert color="yellow" variant="light" mb="sm">
            <b>{marketId}</b> has no active config, so its decision time is unknown. Precompute needs a
            decision time — set a base config for this market on the Config page first.
          </Alert>
        ) : null}

        <Group gap={12} align="flex-end" wrap="nowrap">
          {running ? (
            <Button
              color="red"
              variant="light"
              leftSection={<IconX size={14} />}
              onClick={() => cancelMut.mutate()}
            >
              Cancel
            </Button>
          ) : (
            <Button
              leftSection={<IconPlayerPlay size={14} />}
              loading={runMut.isPending}
              disabled={!hasConfig}
              onClick={() => runMut.mutate()}
            >
              Run precompute
            </Button>
          )}
          <TextInput
            label="Precompute from"
            description="Empty = all history; otherwise every window from this date/time onward"
            type="datetime-local"
            w={260}
            value={startFrom}
            onChange={(e) => setStartFrom(e.currentTarget.value)}
          />
        </Group>

        {status.job ? (
          <Group gap={6} mt="sm">
            {running ? <Loader size={14} /> : null}
            <Badge size="sm" color={jobColor(status.job.status)} variant="light">
              {status.job.status}
            </Badge>
            <Text size="xs" c="dimmed">
              {status.job.model}
              {status.job.written != null ? ` · wrote ${status.job.written}` : ""}
              {status.job.message && status.job.status === "failed" ? ` · ${status.job.message.slice(0, 120)}` : ""}
            </Text>
          </Group>
        ) : null}
      </Card>
    </Stack>
  );
}

export function KronosSettingsPage() {
  const [activeTab, setActiveTab] = useState<string | null>(null);

  const statusQ = useQuery({
    queryKey: ["kronos", "status"],
    queryFn: () => api.kronos.status(),
    // Poll so running jobs + row counts refresh live.
    refetchInterval: 4000,
  });

  if (statusQ.isLoading) return <LoadingState label="Loading Kronos status..." />;
  if (statusQ.isError) return <ErrorState error={statusQ.error as Error} onRetry={() => statusQ.refetch()} />;

  const items = statusQ.data?.items ?? [];
  const currentTab = activeTab ?? items[0]?.market_id ?? null;

  return (
    <Stack gap="md">
      <Group gap="xs" align="center">
        <IconBrain size={20} />
        <Title order={3}>Kronos Forecasts</Title>
        <Tooltip label="Refresh now">
          <Button
            variant="subtle"
            size="compact-sm"
            leftSection={<IconRefresh size={14} />}
            onClick={() => statusQ.refetch()}
          >
            Refresh
          </Button>
        </Tooltip>
      </Group>
      <KronosHorizonCard />

      <Text size="sm" c="dimmed">
        Edit each market's Kronos inference params (model, lookback, timeframe, sampling) and gate
        knobs, then precompute the window forecasts that the simulator and the live gate read from the{" "}
        <code>kronos_predictions</code> cache. The six inference params form the cache signature —
        changing any of them requires a fresh precompute. Requires the <code>kronos-service</code>{" "}
        running.
      </Text>

      {items.length === 0 ? (
        <Alert color="gray" variant="light">No markets found.</Alert>
      ) : (
        <Tabs value={currentTab} onChange={setActiveTab} keepMounted={false}>
          <Tabs.List>
            {items.map((m) => (
              <Tabs.Tab
                key={m.market_id}
                value={m.market_id}
                rightSection={
                  m.active?.kronos_check_enabled ? (
                    <Badge size="xs" color="green" variant="filled" circle> </Badge>
                  ) : null
                }
              >
                <Text span style={monoStyle} size="sm">{m.market_id}</Text>
              </Tabs.Tab>
            ))}
          </Tabs.List>

          {items.map((m) => (
            <Tabs.Panel key={m.market_id} value={m.market_id}>
              {currentTab === m.market_id ? <MarketKronosPanel status={m} /> : null}
            </Tabs.Panel>
          ))}
        </Tabs>
      )}
    </Stack>
  );
}
